/**
 * Per-task-list Y.Doc manager, the tasks sibling of NoteDocManager: one doc
 * per list (tasks in a Y.Map of per-task Y.Maps), refcounted, persisted to
 * IndexedDB for the personal space, online-only for shared folders, synced
 * over the shared socket. Deliberately a parallel class rather than a
 * generalization of NoteDocManager: that file encodes invariant-sensitive
 * naming and is referenced by migrate/transfer/search.
 *
 * Each entry also owns a TaskStore: the reactive facade (dirty-flag cached
 * snapshot for useSyncExternalStore) plus done-transition events, which feed
 * the session-done pile and completion animations without every consumer
 * diffing snapshots itself.
 */
import * as Y from "yjs";
import { IndexeddbPersistence } from "y-indexeddb";
import { docName, readTasks, taskItems, type TaskItem } from "@hydra-notes/shared";
import { crossTabSync } from "./crosstab";
import type { AttachOpts, SyncManager } from "./sync";

export interface DoneTransition {
  taskId: string;
  done: boolean;
  /** True when this tab made the change (drives confetti; remote changes animate but do not celebrate). */
  local: boolean;
}

export class TaskStore {
  private snapshot: TaskItem[] = [];
  private dirty = true;
  private listeners = new Set<() => void>();
  private transitionListeners = new Set<(t: DoneTransition) => void>();
  private prevDone = new Map<string, boolean>();
  private armed = false;

  constructor(readonly doc: Y.Doc) {
    taskItems(doc).observeDeep((_events, tr) => {
      this.dirty = true;
      this.diffDone(tr.local);
      for (const l of this.listeners) l();
    });
  }

  /**
   * Start emitting done-transitions. Called after the initial load so tasks
   * that were already done never replay as fresh completions.
   */
  arm(): void {
    if (this.armed) return;
    this.prevDone.clear();
    taskItems(this.doc).forEach((m, id) => this.prevDone.set(id, (m.get("done") as boolean) ?? false));
    this.armed = true;
  }

  private diffDone(local: boolean): void {
    if (!this.armed) return;
    const seen = new Set<string>();
    const flips: DoneTransition[] = [];
    taskItems(this.doc).forEach((m, id) => {
      seen.add(id);
      const done = (m.get("done") as boolean) ?? false;
      const prev = this.prevDone.get(id);
      // a task arriving already-done (remote create) records without emitting
      if (prev !== undefined && prev !== done) flips.push({ taskId: id, done, local });
      this.prevDone.set(id, done);
    });
    for (const id of [...this.prevDone.keys()]) if (!seen.has(id)) this.prevDone.delete(id);
    for (const flip of flips) for (const l of this.transitionListeners) l(flip);
  }

  subscribe = (cb: () => void): (() => void) => {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  };

  onDoneTransition = (cb: (t: DoneTransition) => void): (() => void) => {
    this.transitionListeners.add(cb);
    return () => this.transitionListeners.delete(cb);
  };

  getSnapshot = (): TaskItem[] => {
    if (this.dirty) {
      this.snapshot = readTasks(this.doc);
      this.dirty = false;
    }
    return this.snapshot;
  };
}

export interface TaskHandle {
  listId: string;
  doc: Y.Doc;
  store: TaskStore;
  whenLoaded: Promise<void>;
  release: () => void;
}

interface Entry {
  doc: Y.Doc;
  store: TaskStore;
  persistence: IndexeddbPersistence | null;
  releaseCrossTab: () => void;
  releaseSync: (() => void) | null;
  whenLoaded: Promise<void>;
  refs: number;
  closeTimer: ReturnType<typeof setTimeout> | null;
}

const CLOSE_DELAY_MS = 30_000; // keep recently-closed lists warm

export interface TaskDocManagerOpts {
  /** false = no IndexedDB (shared folders are online-only). */
  persist?: boolean;
  attach?: Omit<AttachOpts, "spaceId">;
}

export class TaskDocManager {
  private entries = new Map<string, Entry>();
  private readonly persist: boolean;

  constructor(
    readonly workspaceId: string,
    private readonly sync: SyncManager | null = null,
    private readonly opts: TaskDocManagerOpts = {},
  ) {
    this.persist = opts.persist ?? true;
  }

  private dbName(listId: string) {
    return `hn.task.${this.workspaceId}.${listId}`;
  }

  open(listId: string): TaskHandle {
    let entry = this.entries.get(listId);
    if (!entry) {
      const doc = new Y.Doc({ gc: true });
      const persistence = this.persist ? new IndexeddbPersistence(this.dbName(listId), doc) : null;
      const attachment = this.sync
        ? this.sync.attach(docName.task(this.workspaceId, listId), doc, {
            spaceId: this.persist ? undefined : this.workspaceId,
            ...this.opts.attach,
          })
        : null;
      const store = new TaskStore(doc);
      const whenLoaded = persistence
        ? persistence.whenSynced.then(() => undefined)
        : (attachment?.whenSynced ?? Promise.resolve());
      void whenLoaded.then(() => store.arm());
      entry = {
        doc,
        store,
        persistence,
        releaseCrossTab: crossTabSync(this.dbName(listId), doc),
        releaseSync: attachment ? attachment.release : null,
        whenLoaded,
        refs: 0,
        closeTimer: null,
      };
      this.entries.set(listId, entry);
    }
    if (entry.closeTimer) {
      clearTimeout(entry.closeTimer);
      entry.closeTimer = null;
    }
    entry.refs++;

    let released = false;
    return {
      listId,
      doc: entry.doc,
      store: entry.store,
      whenLoaded: entry.whenLoaded,
      release: () => {
        if (released) return;
        released = true;
        entry.refs--;
        if (entry.refs <= 0) {
          entry.closeTimer = setTimeout(() => this.close(listId), CLOSE_DELAY_MS);
        }
      },
    };
  }

  private close(listId: string) {
    const entry = this.entries.get(listId);
    if (!entry || entry.refs > 0) return;
    entry.releaseSync?.();
    entry.releaseCrossTab();
    entry.persistence?.destroy();
    entry.doc.destroy();
    this.entries.delete(listId);
  }

  /** Permanently destroy a list's local storage (after permanent delete). */
  async destroyStorage(listId: string) {
    const entry = this.entries.get(listId);
    if (entry) {
      entry.releaseSync?.();
      entry.releaseCrossTab();
      entry.doc.destroy();
      if (entry.persistence) await entry.persistence.clearData();
      this.entries.delete(listId);
    } else if (this.persist) {
      await new Promise<void>((resolve) => {
        const req = indexedDB.deleteDatabase(this.dbName(listId));
        req.onsuccess = req.onerror = req.onblocked = () => resolve();
      });
    }
  }

  destroyAll() {
    for (const [id] of this.entries) this.close(id);
  }
}
